import { useCallback, useEffect, useRef, useState } from 'react';
import { useClickOutside } from '@common-fe/common-fe';

import { SelectedOrganization } from '@/modules/employer/components/BankingBalance/types';
import { useSingleOrganizationSearch } from './useSingleOrganizationSearch';
import { useOrganizationLevels } from './useOrganizationLevels';

interface Props {
  value: SelectedOrganization | null;
  onChange: (value: SelectedOrganization | null) => void;
}

export const useSingleOrganizationSelector = ({ value, onChange }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isShown, setIsShown] = useState(false);
  useClickOutside(ref as any, () => setIsShown(false));

  useEffect(() => {
    if (isShown && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isShown]);

  const {
    searchString,
    setSearchString,
    searchValue,
    isSearchUsed,
  } = useSingleOrganizationSearch();

  const {
    id,
    type,
    name,
    isSystemLevel,
    isPartnerLevel,
    isDistributorLevel,
    isCompanyLevel,
    currentOrgLevels,
    systemId,
  } = useOrganizationLevels();

  const [partnerParentId, setPartnerParentId] = useState('');
  const [distributorParentId, setDistributorParentId] = useState('');

  const [selectedTemp, setSelectedTemp] = useState<SelectedOrganization | null>(null);

  useEffect(() => {
    setPartnerParentId('');
    setDistributorParentId('');
  }, [searchValue]);

  const handleApply = useCallback(() => {
    onChange(selectedTemp);
    setIsShown(false);
  }, [selectedTemp, onChange]);

  const handleCancel = useCallback(() => {
    setSelectedTemp(value);
    setIsShown(false);
  }, [value]);

  useEffect(() => {
    setSelectedTemp(value);
  }, [value, isShown]);

  return {
    ref,
    inputRef,
    isShown,
    setIsShown,
    searchString,
    setSearchString,
    searchValue,
    isSearchUsed,
    partnerParentId,
    setPartnerParentId,
    distributorParentId,
    setDistributorParentId,
    selectedTemp,
    setSelectedTemp,
    id,
    type,
    name,
    isSystemLevel,
    isPartnerLevel,
    isDistributorLevel,
    isCompanyLevel,
    currentOrgLevels,
    systemId,
    handleApply,
    handleCancel,
  };
};

export default useSingleOrganizationSelector;
